import {
  Container,
  SimpleGrid,
  Stack,
  Text,
  ThemeIcon,
  useComputedColorScheme,
} from "@mantine/core";
import {
  IconShieldCheck,
  IconLayoutGrid,
  IconHeadset,
} from "@tabler/icons-react";
import { motion } from "framer-motion";
import { CountUp } from "./CountUp";

interface TrustSignal {
  icon: typeof IconShieldCheck;
  headline: string;
  /** When present, the numeric part of the headline counts up on scroll. */
  count?: { value: number; decimals?: number; suffix: string };
  description: string;
  color: string;
}

const signals: TrustSignal[] = [
  {
    icon: IconShieldCheck,
    headline: "HIPAA-Ready",
    description: "Built for healthcare compliance from day one",
    color: "#4a8c71",
  },
  {
    icon: IconLayoutGrid,
    headline: "14 Core Modules",
    count: { value: 14, suffix: " Core Modules" },
    description: "Residents · Staff · Incidents · Forms · and more",
    color: "#4c6ef5",
  },
  {
    icon: IconHeadset,
    headline: "24-Hour Response",
    count: { value: 24, suffix: "-Hour Response" },
    description: "Onboarding & email support included with every plan",
    color: "#12b886",
  },
];

const ease = [0.25, 0.46, 0.45, 0.94] as const;

const stagger = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.12, delayChildren: 0.05 } },
};

const fadeUp = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.55, ease } },
};

export function SocialProofBar() {
  const computedColorScheme = useComputedColorScheme("light");

  return (
    <motion.section
      aria-label="Social proof statistics"
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, amount: 0.3 }}
      variants={stagger}
      style={{
        padding: "48px 0",
        background:
          computedColorScheme === "dark"
            ? "linear-gradient(135deg, #1A1B1E 0%, #25262B 100%)"
            : "linear-gradient(135deg, #f0f7f4 0%, #e6fcf5 100%)",
        borderTop:
          computedColorScheme === "dark"
            ? "1px solid #373A40"
            : "1px solid #b8ddd1",
        borderBottom:
          computedColorScheme === "dark"
            ? "1px solid #373A40"
            : "1px solid #b8ddd1",
      }}
    >
      <Container size="xl">
        <SimpleGrid cols={{ base: 1, sm: 3 }} spacing="xl">
          {signals.map((signal) => {
            const Icon = signal.icon;
            return (
              <motion.div key={signal.headline} variants={fadeUp}>
                <Stack align="center" gap="xs">
                  <ThemeIcon
                    size={48}
                    radius="md"
                    variant="light"
                    style={{
                      backgroundColor: `${signal.color}18`,
                      color: signal.color,
                    }}
                  >
                    <Icon size={26} stroke={1.5} />
                  </ThemeIcon>
                  <Text fw={700} size="md" ta="center">
                    {signal.count ? (
                      <>
                        <CountUp
                          value={signal.count.value}
                          decimals={signal.count.decimals}
                        />
                        {signal.count.suffix}
                      </>
                    ) : (
                      signal.headline
                    )}
                  </Text>
                  <Text
                    size="xs"
                    c="dimmed"
                    ta="center"
                    style={{ lineHeight: 1.5 }}
                  >
                    {signal.description}
                  </Text>
                </Stack>
              </motion.div>
            );
          })}
        </SimpleGrid>
      </Container>
    </motion.section>
  );
}
